import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ActiveCase {
  id: string;
  title: string;
  status: string;
  updated_at: string;
}

/**
 * Hook to fetch active (non-completed) cases for the current user.
 * Used for sidebar module navigation and case switching dropdown.
 */
export function useActiveCases() {
  const [cases, setCases] = useState<ActiveCase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCases = async () => {
      try {
        const { data, error } = await supabase
          .from('audits')
          .select('id, title, status, updated_at')
          .neq('status', 'completed')
          .order('updated_at', { ascending: false })
          .limit(15);

        if (error) {
          console.error('[useActiveCases] Error fetching cases:', error);
          setCases([]);
        } else {
          setCases(data || []);
        }
      } catch (err) {
        console.error('[useActiveCases] Unexpected error:', err);
        setCases([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCases();

    // Subscribe to changes
    const channel = supabase
      .channel('active-cases-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'audits' },
        () => {
          fetchCases();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { cases, loading };
}
